import { lazy } from 'react';
import { IconNote } from '@tabler/icons-react';

import { NoteDetailContent } from '@/components/notes/NoteDetailPanel/NoteDetailContent';
import { NoteFormPanel } from '@/components/notes/NoteFormPanel/NoteFormPanel';
import { selectHasActiveNoteFilters } from '@/store/selectors/noteSelectors';
import { selectArtifactNotesVisible } from '@/store/selectors/layerSelectors';
import type { GeoMarkupStore } from '@/store';
import { NOTE_VIEW_MODE, type NoteViewMode, type ViewMode } from '@/types';

import type { ArtifactDefinition } from './registry';

/**
 * `BackgroundPage` is lazy-loaded to break the registry → notes → page →
 * hooks → registry import cycle that would otherwise form and to keep the
 * initial bundle small.
 */
const NotesPage = lazy(() =>
  import('@/pages/NotesPage/NotesPage').then((module) => ({
    default: module.NotesPage,
  }))
);

/**
 * `PanelContent` is lazy-loaded to break the registry → notes →
 * NotesPanelContent → @/store → formConflictSlice → @/artifacts → registry
 * cycle. Eager evaluation put `NotesPanelContent` in TDZ when `notesArtifact`
 * referenced it, crashing the app at boot.
 */
const NotesPanelContent = lazy(() =>
  import('@/components/ui/SidePanel/NotesPanelContent').then((module) => ({
    default: module.NotesPanelContent,
  }))
);

export const notesArtifact: ArtifactDefinition = {
  id: 'notes',
  singular: 'note',
  icon: IconNote,
  labelKey: 'appShell.sidebar.notes',
  supportedViews: [NOTE_VIEW_MODE.MAP, NOTE_VIEW_MODE.LIST] as const,
  defaultView: NOTE_VIEW_MODE.MAP,
  BackgroundPage: NotesPage,
  PanelContent: NotesPanelContent,
  DetailContent: NoteDetailContent,
  FormPanel: NoteFormPanel,
  getSlice: (state) => state.notes,
  selectors: {
    hasActiveFilters: selectHasActiveNoteFilters,
    isLayerVisible: selectArtifactNotesVisible,
  },
  syncViewMode: (view: ViewMode, store: GeoMarkupStore): void => {
    if (store.ui.noteViewMode !== view) {
      store.ui.setNoteViewMode(view as NoteViewMode);
    }
  },
};
